/**
 * Timezone Utility Functions
 * Handles timezone conversions and formatting for appointment scheduling
 * Uses JavaScript's native Intl API for comprehensive world timezone support
 */

/**
 * Format datetime-local input to timezone-aware ISO string
 * @param datetimeLocal - The datetime-local input value (e.g., '2025-08-20T04:00')
 * @param timezone - The patient's clinic timezone (e.g., 'US/Eastern', 'Europe/London', 'Asia/Tokyo')
 * @returns Timezone-aware ISO string (e.g., '2025-08-20T04:00:00.000-04:00')
 */
export const formatAppointmentDateTime = (datetimeLocal: string, timezone: string): string => {
  if (!datetimeLocal) return '';
  
  try {
    // Check if the input is already an ISO string with timezone
    if (datetimeLocal.includes('T') && (datetimeLocal.includes('+') || datetimeLocal.includes('Z') || datetimeLocal.match(/-\d{2}:\d{2}$/))) {
      // Already formatted, return as is
      return datetimeLocal;
    }
    
    // Handle datetime-local input (YYYY-MM-DDTHH:MM format)
    // Parse the input components
    const [datePart, timePart = '00:00'] = datetimeLocal.includes('T') ? datetimeLocal.split('T') : [datetimeLocal, '00:00'];
    const [year, month, day] = datePart.split('-').map(Number);
    const [hours, minutes] = timePart.split(':').map(Number);
    
    // Create a date object representing the local time in the target timezone
    const testDate = new Date(year, month - 1, day, hours, minutes);
    
    // Get the actual timezone offset for this specific date/time
    const offset = getTimezoneOffsetForDate(testDate, timezone);
    
    // Add seconds if not present
    const formattedInput = timePart.split(':').length === 2 ? `${datetimeLocal}:00` : datetimeLocal;
    
    console.log(`Formatting appointment: ${formattedInput} in ${timezone} -> ${formattedInput}.000${offset}`);
    
    return `${formattedInput}.000${offset}`;
  } catch (error) {
    console.error('Error formatting appointment datetime:', error);
    // Fallback to Eastern Time
    return `${datetimeLocal}:00.000-05:00`;
  }
};

/**
 * Get timezone offset for a specific date and timezone
 * @param date - The date to get offset for
 * @param timezone - The timezone (e.g., 'US/Eastern', 'Europe/London')
 * @returns Timezone offset string (e.g., '-04:00', '+01:00')
 */
export const getTimezoneOffsetForDate = (date: Date, timezone: string): string => {
  try {
    // Use Intl.DateTimeFormat to get timezone offset
    const formatter = new Intl.DateTimeFormat('en', {
      timeZone: timezone,
      timeZoneName: 'longOffset'
    });
    
    const parts = formatter.formatToParts(date);
    const offsetPart = parts.find(part => part.type === 'timeZoneName');
    
    if (offsetPart && offsetPart.value.startsWith('GMT')) {
      // Convert GMT+/-X to +/-XX:XX format
      const gmtOffset = offsetPart.value.replace('GMT', '');
      if (gmtOffset === '') return '+00:00';
      
      // Handle formats like GMT-5, GMT+5:30, etc.
      const sign = gmtOffset.charAt(0);
      const timeStr = gmtOffset.slice(1);
      
      if (timeStr.includes(':')) {
        // Already in HH:MM format
        return `${sign}${timeStr.padStart(5, '0')}`;
      } else {
        // Convert single digit or double digit to HH:MM
        const hours = parseInt(timeStr) || 0;
        return `${sign}${hours.toString().padStart(2, '0')}:00`;
      }
    }
    
    // Fallback: calculate offset manually
    const utcDate = new Date(date.toISOString());
    const localDate = new Date(date.toLocaleString('en-US', { timeZone: timezone }));
    const offsetMinutes = (utcDate.getTime() - localDate.getTime()) / (1000 * 60);
    const offsetHours = Math.floor(Math.abs(offsetMinutes) / 60);
    const offsetMins = Math.abs(offsetMinutes) % 60;
    const sign = offsetMinutes <= 0 ? '+' : '-';
    
    return `${sign}${offsetHours.toString().padStart(2, '0')}:${offsetMins.toString().padStart(2, '0')}`;
  } catch (error) {
    console.error('Error getting timezone offset:', error);
    // Fallback based on common timezone patterns
    return getStaticTimezoneOffset(timezone);
  }
};

/**
 * Static timezone offset mapping for fallback
 * @param timezone - The timezone string
 * @returns Static timezone offset
 */
const getStaticTimezoneOffset = (timezone: string): string => {
  const staticOffsets: { [key: string]: string } = {
    // US Timezones (Standard Time offsets - will be adjusted for DST automatically)
    'US/Pacific': '-08:00',      // PST (Standard Time)
    'US/Mountain': '-07:00',     // MST
    'US/Central': '-06:00',      // CST
    'US/Eastern': '-05:00',      // EST
    'US/Alaska': '-09:00',       // AKST
    'US/Hawaii': '-10:00',       // HST (no DST)
    
    // Americas (Standard Time offsets)
    'America/Los_Angeles': '-08:00',  // PST
    'America/Denver': '-07:00',       // MST
    'America/Chicago': '-06:00',      // CST
    'America/New_York': '-05:00',     // EST
    'America/Anchorage': '-09:00',    // AKST
    'America/Phoenix': '-07:00',      // MST (no DST)
    'America/Toronto': '-05:00',      // EST
    'America/Vancouver': '-08:00',    // PST
    
    // Europe
    'Europe/London': '+00:00',
    'Europe/Paris': '+01:00',
    'Europe/Berlin': '+01:00',
    'Europe/Rome': '+01:00',
    'Europe/Madrid': '+01:00',
    'Europe/Amsterdam': '+01:00',
    'Europe/Brussels': '+01:00',
    'Europe/Vienna': '+01:00',
    'Europe/Zurich': '+01:00',
    'Europe/Stockholm': '+01:00',
    'Europe/Oslo': '+01:00',
    'Europe/Copenhagen': '+01:00',
    'Europe/Helsinki': '+02:00',
    'Europe/Athens': '+02:00',
    'Europe/Istanbul': '+03:00',
    'Europe/Moscow': '+03:00',
    
    // Asia
    'Asia/Tokyo': '+09:00',
    'Asia/Shanghai': '+08:00',
    'Asia/Hong_Kong': '+08:00',
    'Asia/Singapore': '+08:00',
    'Asia/Seoul': '+09:00',
    'Asia/Kolkata': '+05:30',
    'Asia/Mumbai': '+05:30',
    'Asia/Delhi': '+05:30',
    'Asia/Dubai': '+04:00',
    'Asia/Bangkok': '+07:00',
    'Asia/Jakarta': '+07:00',
    'Asia/Manila': '+08:00',
    
    // Australia
    'Australia/Sydney': '+10:00',
    'Australia/Melbourne': '+10:00',
    'Australia/Brisbane': '+10:00',
    'Australia/Perth': '+08:00',
    'Australia/Adelaide': '+09:30',
    'Australia/Darwin': '+09:30',
    
    // Others
    'UTC': '+00:00',
    'GMT': '+00:00'
  };
  
  return staticOffsets[timezone] || '-05:00'; // Default to Eastern Time
};

/**
 * Convert UTC datetime to specific timezone for display
 * @param utcDatetime - UTC datetime string
 * @param timezone - Target timezone (e.g., 'US/Pacific')
 * @returns Formatted date and time in target timezone
 */
export const convertUTCToTimezone = (utcDatetime: string, timezone: string): {
  date: string;
  time: string;
  fullDateTime: string;
} => {
  if (!utcDatetime) {
    return {
      date: 'No date set',
      time: 'No time set',
      fullDateTime: 'No appointment set'
    };
  }

  try {
    // Ensure proper UTC parsing (handle missing 'T' and timezone info)
    const hasTZ = /[+-]\d{2}:\d{2}$/.test(utcDatetime) || utcDatetime.endsWith('Z');
    const normalized = (hasTZ ? utcDatetime : `${utcDatetime.replace(' ', 'T')}Z`).replace(' ', 'T');
    const utcDate = new Date(normalized);
    
    const date = utcDate.toLocaleDateString('en-US', {
      timeZone: timezone,
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    
    const time = utcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    const timezoneAbbr = getTimezoneAbbreviation(timezone);
    const fullDateTime = `${date} at ${time} (${timezoneAbbr})`;
    
    return { date, time, fullDateTime };
  } catch (error) {
    console.error('Error converting UTC to timezone:', error);
    return {
      date: 'Invalid date',
      time: 'Invalid time',
      fullDateTime: 'Invalid appointment'
    };
  }
};

/**
 * Format datetime in MM/DD/YYYY HH:MM AA XXX format for specific timezone
 * @param dateString - The datetime string to format
 * @param timezone - The target timezone
 * @returns Formatted string in MM/DD/YYYY HH:MM AA XXX format
 */
export const formatDateTimeWithTimezone = (dateString: string, timezone: string): string => {
  if (!dateString) return 'Appointment Not Set';
  
  try {
    const hasTZ = /[+-]\d{2}:\d{2}$/.test(dateString) || dateString.endsWith('Z');
    const normalized = (hasTZ ? dateString : `${dateString.replace(' ', 'T')}Z`).replace(' ', 'T');
    const utcDate = new Date(normalized);
    
    const date = utcDate.toLocaleDateString('en-US', {
      timeZone: timezone,
      month: '2-digit',
      day: '2-digit',
      year: 'numeric'
    });
    
    const time = utcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    return `${date} ${time} ${timezone}`;
  } catch (error) {
    console.error('Error formatting datetime with timezone:', error);
    return 'Invalid appointment';
  }
};

/**
 * Format datetime in MM/DD/YY HH:MM AM/PM format for specific timezone (compact format)
 * @param dateString - The datetime string to format
 * @param timezone - The target timezone
 * @returns Formatted string in MM/DD/YY HH:MM AM format (no timezone abbreviation)
 */
export const formatDateTimeCompact = (dateString: string, timezone: string): string => {
  if (!dateString) return 'Appointment Not Set';
  
  try {
    const hasTZ = /[+-]\d{2}:\d{2}$/.test(dateString) || dateString.endsWith('Z');
    const normalized = (hasTZ ? dateString : `${dateString.replace(' ', 'T')}Z`).replace(' ', 'T');
    const utcDate = new Date(normalized);
    
    const date = utcDate.toLocaleDateString('en-US', {
      timeZone: timezone,
      month: '2-digit',
      day: '2-digit',
      year: '2-digit'
    });
    
    const time = utcDate.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
    
    return `${date} ${time}`;
  } catch (error) {
    console.error('Error formatting datetime compact:', error);
    return 'Invalid appointment';
  }
};

/**
 * Get timezone abbreviation for display
 * @param timezone - The timezone string
 * @returns Timezone abbreviation (e.g., 'PST', 'EST')
 */
export const getTimezoneAbbreviation = (timezone: string): string => {
  const abbreviationMap: { [key: string]: string } = {
    'US/Pacific': 'PST',
    'America/Los_Angeles': 'PST',
    'US/Mountain': 'MST',
    'America/Denver': 'MST',
    'America/Phoenix': 'MST',
    'US/Central': 'CST',
    'America/Chicago': 'CST',
    'US/Eastern': 'EST',
    'America/New_York': 'EST',
    'US/Alaska': 'AKST',
    'America/Anchorage': 'AKST',
    'US/Hawaii': 'HST',
    'Pacific/Honolulu': 'HST',
    'Europe/London': 'GMT',
    'Europe/Paris': 'CET',
    'Europe/Berlin': 'CET',
    'Asia/Tokyo': 'JST',
    'Asia/Shanghai': 'CST',
    'Asia/Kolkata': 'IST',
    'Asia/Calcutta': 'IST',
    'Asia/Mumbai': 'IST',
    'Asia/Delhi': 'IST',
    'Asia/Dubai': 'GST',
    'Australia/Sydney': 'AEST'
  };
  
  // Try to get dynamic abbreviation if not in static map
  if (!abbreviationMap[timezone]) {
    try {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en', {
        timeZone: timezone,
        timeZoneName: 'short'
      });
      const parts = formatter.formatToParts(now);
      const timeZonePart = parts.find(part => part.type === 'timeZoneName');
      if (timeZonePart) {
        return timeZonePart.value;
      }
    } catch (error) {
      console.warn(`Could not get abbreviation for timezone: ${timezone}`);
    }
  }
  
  return abbreviationMap[timezone] || 'UTC';
};

/**
 * Get all supported timezones
 * @returns Array of supported timezone objects
 */
export const getSupportedTimezones = (): Array<{ value: string; label: string; offset: string }> => {
  const staticOffsets: { [key: string]: string } = {
    // US Timezones (Standard Time offsets)
    'US/Pacific': '-08:00',      // PST
    'US/Mountain': '-07:00',     // MST
    'US/Central': '-06:00',      // CST
    'US/Eastern': '-05:00',      // EST
    'US/Alaska': '-09:00',       // AKST
    'US/Hawaii': '-10:00',       // HST (no DST)
    
    // Americas (Standard Time offsets)
    'America/Los_Angeles': '-08:00',  // PST
    'America/Denver': '-07:00',       // MST
    'America/Chicago': '-06:00',      // CST
    'America/New_York': '-05:00',     // EST
    'America/Anchorage': '-09:00',    // AKST
    'America/Phoenix': '-07:00',      // MST (no DST)
    'America/Toronto': '-05:00',      // EST
    'America/Vancouver': '-08:00',    // PST
    
    // Europe
    'Europe/London': '+00:00',
    'Europe/Paris': '+01:00',
    'Europe/Berlin': '+01:00',
    'Europe/Rome': '+01:00',
    'Europe/Madrid': '+01:00',
    'Europe/Amsterdam': '+01:00',
    'Europe/Brussels': '+01:00',
    'Europe/Vienna': '+01:00',
    'Europe/Zurich': '+01:00',
    'Europe/Stockholm': '+01:00',
    'Europe/Oslo': '+01:00',
    'Europe/Copenhagen': '+01:00',
    'Europe/Helsinki': '+02:00',
    'Europe/Athens': '+02:00',
    'Europe/Istanbul': '+03:00',
    'Europe/Moscow': '+03:00',
    
    // Asia
    'Asia/Tokyo': '+09:00',
    'Asia/Shanghai': '+08:00',
    'Asia/Hong_Kong': '+08:00',
    'Asia/Singapore': '+08:00',
    'Asia/Seoul': '+09:00',
    'Asia/Kolkata': '+05:30',
    'Asia/Mumbai': '+05:30',
    'Asia/Delhi': '+05:30',
    'Asia/Dubai': '+04:00',
    'Asia/Bangkok': '+07:00',
    'Asia/Jakarta': '+07:00',
    'Asia/Manila': '+08:00',
    
    // Australia
    'Australia/Sydney': '+10:00',
    'Australia/Melbourne': '+10:00',
    'Australia/Brisbane': '+10:00',
    'Australia/Perth': '+08:00',
    'Australia/Adelaide': '+09:30',
    'Australia/Darwin': '+09:30'
  };
  
  return Object.keys(staticOffsets).map(timezone => ({
    value: timezone,
    label: `${timezone} (${getTimezoneAbbreviation(timezone)})`,
    offset: staticOffsets[timezone]
  })).sort((a, b) => a.label.localeCompare(b.label));
};